/**
 * @file Builds one export column per tier class shared across a global project's linked funds.
 */
import keyBy from "lodash.keyby";

import getInvestmentAmountForProjectTier from "./getInvestmentAmountForProjectTier";
import type { Column, Project, Investment, GetTierTitle } from "../types";

const EMPTY_VALUE = "-";

/**
 * Builds one export column per distinct tier title across the given projects, each column
 * showing the invested amount for an investment that matches that tier in its own project.
 */
const getGlobalInvestmentClassColumns = (projects: Project[], getTierTitle: GetTierTitle): Column[] => {
  const projectsMap: Record<string, Project> = keyBy(projects, "globalProjectId");

  /**
   * Checks whether an investment's tier, resolved through the global project id, matches the given column title.
   */
  const isClassMatched = (
    organizationId: string,
    columnTitle: string,
    tierId: string,
    investment: Investment,
  ): boolean => {
    const { projectId } = investment;
    const globalProjectId = `${organizationId}-${projectId}`;

    const project = projectsMap[globalProjectId];
    const { tiers } = project;

    const tier = tiers.find(({ name }) => name === tierId);

    if (!tier) {
      return false;
    }

    const title = getTierTitle(tier).toLowerCase();
    const isMatched = columnTitle === title;

    return isMatched;
  };

  const columnsMap: Record<string, Column> = {};

  for (const project of projects) {
    const { tiers: projectTiers } = project;

    for (const tier of projectTiers) {
      const { name: tierId } = tier;

      const columnTitle = getTierTitle(tier).toLowerCase();

      /**
       * Returns the invested amount for this tier's column, or the empty-value placeholder when the investment does not match the tier.
       */
      const value = (investment: Investment, organizationId: string): string => {
        const isMatched = isClassMatched(organizationId, columnTitle, tierId, investment);

        if (!isMatched) {
          return EMPTY_VALUE;
        }

        const amount = getInvestmentAmountForProjectTier(tierId, investment);

        return amount;
      };

      const title = getTierTitle(tier);
      const column = columnsMap[columnTitle];

      if (!column) {
        columnsMap[columnTitle] = {
          title,
          value,
        };
      }
    }
  }

  const columns = Object.values(columnsMap);

  return columns;
};

export default getGlobalInvestmentClassColumns;
